import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field, GoldButton, GhostButton, NativeSelect } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { usePositions } from "@/lib/positions";
import { fmtDateTime, fmtRange, VAGUE_TIMES } from "@/lib/format";
import { toLocalInput, fromLocalInput, fmtMinutes } from "@/lib/time";

const gmaps = (g) => `https://www.google.com/maps/search/?api=1&query=${g.lat},${g.lng}`;
const atTime = (date, time) => (date && time && !VAGUE_TIMES.includes(time) ? `${date}T${time}` : "");

const TargetPicker = ({ pick, setPick, events, employees, onTimes }) => {
  const { t } = useLang();
  const { label } = usePositions();
  const ev = (events || []).find((e) => e.id === pick.event_id);
  const chooseSlot = (e, slotId) => {
    const sl = e?.positions.find((s) => s.id === slotId);
    setPick((p) => ({ ...p, event_id: e?.id || "", slot_id: sl?.id || "" }));
    if (sl) onTimes(atTime(e.date, sl.start_time || e.start_time), atTime(e.date, sl.end_time || e.end_time));
  };
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="ts-target-picker">
      <Field label={t("t.pickEvent")} className="sm:col-span-2">
        <NativeSelect testid="ts-pick-event" value={pick.event_id} onChange={(v) => { const e = events.find((x) => x.id === v); chooseSlot(e, e?.positions[0]?.id); }}>
          <option value="">—</option>
          {(events || []).map((e) => <option key={e.id} value={e.id}>{e.date} · {e.name}</option>)}
        </NativeSelect>
      </Field>
      <Field label={t("t.pickSlot")}>
        <NativeSelect testid="ts-pick-slot" value={pick.slot_id} onChange={(v) => chooseSlot(ev, v)}>
          {(ev?.positions || []).map((s) => <option key={s.id} value={s.id}>{label(s.position_key)} · {fmtRange(s.start_time || ev.start_time, s.end_time || ev.end_time, t)}</option>)}
        </NativeSelect>
      </Field>
      <Field label={t("t.pickEmployee")}>
        <NativeSelect testid="ts-pick-employee" value={pick.employee_id} onChange={(v) => setPick((p) => ({ ...p, employee_id: v }))}>
          <option value="">—</option>
          {(employees || []).map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </NativeSelect>
      </Field>
      <p className="text-xs text-neutral-500 sm:col-span-2 -mt-2">{t("t.autoAssign")}</p>
    </div>
  );
};

export const TimesheetDialog = ({ entry, onClose, onSaved, events, employees }) => {
  const { t, lang } = useLang();
  const isNew = entry && !entry.id;
  const picking = isNew && !entry.assignment_id;
  const [f, setF] = useState({ clock_in: "", clock_out: "", break_minutes: 0, total_override_minutes: "", reason: "" });
  const [pick, setPick] = useState({ event_id: "", slot_id: "", employee_id: "" });
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setConfirmDel(false);
    setPick({ event_id: "", slot_id: "", employee_id: "" });
    setF({
      clock_in: entry.clock_in ? toLocalInput(entry.clock_in) : atTime(entry.date, entry.scheduled_start),
      clock_out: entry.clock_out ? toLocalInput(entry.clock_out) : isNew ? atTime(entry.date, entry.scheduled_end) : "",
      break_minutes: entry.break_minutes || 0,
      total_override_minutes: entry.total_override_minutes ?? "",
      reason: "",
    });
  }, [entry, isNew]);

  const remove = async () => {
    if (!confirmDel) {
      setConfirmDel(true);
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/timesheets/${entry.id}`);
      toast.success(t("t.deleted"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    const payload = {
      clock_in: fromLocalInput(f.clock_in), clock_out: fromLocalInput(f.clock_out), break_minutes: Number(f.break_minutes) || 0,
      total_override_minutes: f.total_override_minutes === "" ? null : Number(f.total_override_minutes), reason: f.reason,
    };
    try {
      if (picking) await api.post("/timesheets", { ...payload, ...pick });
      else if (isNew) await api.post("/timesheets", { ...payload, assignment_id: entry.assignment_id });
      else await api.patch(`/timesheets/${entry.id}`, payload);
      toast.success(t("c.saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-dialog">
        {entry && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl">{isNew ? t("t.addEntry") : t("t.edit")}</DialogTitle>
              {!picking && <p className="text-sm text-neutral-500">{entry.employee_name} · {entry.event_name} · <span className="mono">{fmtRange(entry.scheduled_start, entry.scheduled_end, t)}</span></p>}
            </DialogHeader>
            {picking && <TargetPicker pick={pick} setPick={setPick} events={events} employees={employees} onTimes={(ci, co) => setF((x) => ({ ...x, clock_in: ci || x.clock_in, clock_out: co || x.clock_out }))} />}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("t.in")}><Input data-testid="ts-clock-in-input" type="datetime-local" value={f.clock_in} onChange={set("clock_in")} className="h-10 rounded-none" /></Field>
              <Field label={t("t.out")}><Input data-testid="ts-clock-out-input" type="datetime-local" value={f.clock_out} onChange={set("clock_out")} className="h-10 rounded-none" /></Field>
              <Field label={`${t("t.break")} (min)`}><Input data-testid="ts-break-input" type="number" min={0} value={f.break_minutes} onChange={set("break_minutes")} className="h-10 rounded-none mono" /></Field>
              <Field label={t("t.override")}><Input data-testid="ts-override-input" type="number" min={0} value={f.total_override_minutes} onChange={set("total_override_minutes")} className="h-10 rounded-none mono" /></Field>
              <Field label={t("t.reason")} className="sm:col-span-2"><Textarea data-testid="ts-reason-input" rows={2} value={f.reason} onChange={set("reason")} className="rounded-none" /></Field>
            </div>
            {!isNew && (entry.in_geo || entry.out_geo) && (
              <div className="flex flex-wrap gap-4 text-xs text-neutral-500">
                {[["t.in", entry.in_geo], ["t.out", entry.out_geo]].map(([k, g]) => g && (
                  <a key={k} href={gmaps(g)} target="_blank" rel="noreferrer" className="hover:text-black underline underline-offset-4">{t(k)} · {g.distance_m ?? "?"} m{g.site && ` · ${t(`t.site.${g.site}`)}`} · {t("t.mapCheck")}</a>
                ))}
              </div>
            )}
            {!isNew && entry.history?.length > 0 && (
              <div data-testid="ts-history">
                <div className="eyebrow mb-2">{t("t.history")}</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {[...entry.history].reverse().map((h, i) => (
                    <div key={i} className="text-xs border-b border-white/[0.05] pb-2">
                      <div><span className="font-medium">{t(`t.field.${h.field}`)}</span>{h.field !== "approved" && <>: <span className="mono text-neutral-500 line-through">{h.old ?? "—"}</span> → <span className="mono">{h.new ?? "—"}</span></>}</div>
                      <div className="text-neutral-500 mt-0.5">{h.by_name} · {fmtDateTime(h.at, lang)}{h.reason ? ` · « ${h.reason} »` : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter className="items-center gap-2">
              {!isNew && <span className="text-xs text-neutral-500 mr-auto">{t("t.total")} : <span className="mono">{fmtMinutes(entry.total_minutes)}</span></span>}
              {!isNew && (
                <button type="button" data-testid="ts-delete-btn" disabled={busy} onClick={remove}
                  className={`press h-10 px-4 rounded-full text-sm border ${confirmDel ? "bg-red-600 border-red-600 text-white hover:bg-red-500" : "border-red-400/60 text-red-500 hover:bg-red-500/10"}`}>
                  {confirmDel ? t("t.deleteConfirm") : t("t.delete")}
                </button>
              )}
              <GhostButton onClick={onClose} data-testid="ts-cancel-btn">{t("c.cancel")}</GhostButton>
              <GoldButton onClick={save} disabled={busy || !f.reason.trim() || !f.clock_in || (picking && !(pick.event_id && pick.slot_id && pick.employee_id))} data-testid="ts-save-btn">{t("c.save")}</GoldButton>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
